import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Copy, ExternalLink, KeyRound, Trash2 } from 'lucide-react'
import { AuthContext } from '@retink/web-auth'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDestructive,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  FormError,
  Input,
  LoadingState,
  PageHeader,
  statusTone,
  toast,
  useMutation,
  useResource,
} from '@retink/web-ui'
import { api, USER_SIGN_IN_URL } from '../../lib/api'

interface OrgIdp {
  id: string
  kind: string
  name: string
  domains: string[]
  status: string
}

interface OwnerSetup {
  idpId: string
  ownerEmail: string
  setupUrl: string
  expiresAt: string
}

const BLANK = { domain: '', ownerEmail: '' }

export function OrgIdp() {
  const { orgId } = useParams<{ orgId: string }>()
  const { user } = useContext(AuthContext)
  const canManage = user?.platformRole === 'admin' || user?.orgRole === 'admin'
  const [show, setShow] = useState(false)
  const [removing, setRemoving] = useState<OrgIdp | null>(null)
  const [form, setForm] = useState(BLANK)
  const [setup, setSetup] = useState<OwnerSetup | null>(null)

  const { data: idps = [], loading, error, reload } = useResource(
    () => api.get<OrgIdp[]>(`/api/orgs/${orgId}/idp`),
    [orgId],
  )

  const connect = useMutation(
    () => api.post<OwnerSetup>(`/api/orgs/${orgId}/idp/microsoft-owner-setup`, form),
    { fallbackError: 'Could not create Microsoft setup' },
  )
  const removeIdp = useMutation((id: string) => api.delete(`/api/orgs/${orgId}/idp/${id}`), {
    fallbackError: 'Failed to disable provider',
  })

  async function copy(value: string, message: string) {
    await navigator.clipboard?.writeText(value)
    toast.success(message)
  }

  function openConnect() {
    connect.reset()
    setForm(BLANK)
    setShow(true)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const created = await connect.mutate()
    if (!created) return
    setSetup(created)
    setShow(false)
    setForm(BLANK)
    toast.success('Owner setup link created')
    reload()
  }

  return (
    <div className="p-8 max-w-3xl">
      <PageHeader
        title="Identity"
        description="Microsoft sign-in for this organization. Active providers apply to all its workspaces automatically."
        action={canManage ? <Button onClick={openConnect}>Connect Microsoft</Button> : undefined}
      />

      <div className="space-y-4">
        {setup && (
          <Card className="overflow-hidden border-primary/40">
            <CardHeader>Owner setup link ready</CardHeader>
            <div className="space-y-3 px-4 pb-4 text-sm">
              <p>
                Send this link to <span className="font-medium">{setup.ownerEmail}</span>. They open it without a
                platform login, approve Microsoft access, then sign in to become organization owner.
              </p>
              <p className="text-xs text-muted-foreground">
                One-time link. Expires {new Date(setup.expiresAt).toLocaleString()}.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input value={setup.setupUrl} readOnly aria-label="Owner setup URL" className="font-mono text-xs" />
                <Button type="button" variant="outline" onClick={() => void copy(setup.setupUrl, 'Setup link copied')}>
                  <Copy /> Copy
                </Button>
                <Button type="button" variant="outline" onClick={() => window.open(setup.setupUrl, '_blank', 'noopener,noreferrer')}>
                  <ExternalLink /> Open
                </Button>
              </div>
            </div>
          </Card>
        )}

        <Card className="overflow-hidden">
          <CardHeader>User sign-in</CardHeader>
          <div className="space-y-2 px-4 pb-4">
            {idps.some((idp) => idp.status === 'active') ? (
              <>
                <p className="text-sm text-muted-foreground">
                  After setup, everyone uses this normal sign-in page. Retink selects Microsoft from their email domain.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input value={USER_SIGN_IN_URL} readOnly aria-label="User sign-in URL" className="font-mono text-xs" />
                  <Button type="button" variant="outline" onClick={() => void copy(USER_SIGN_IN_URL, 'Sign-in URL copied')}>
                    <Copy /> Copy
                  </Button>
                  <Button type="button" variant="outline" onClick={() => window.open(USER_SIGN_IN_URL, '_blank', 'noopener,noreferrer')}>
                    <ExternalLink /> Open
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Available after the first owner completes Microsoft consent and sign-in.
              </p>
            )}
          </div>
        </Card>

        <div className="space-y-3">
          {loading && <Card className="p-6"><LoadingState /></Card>}
          {!loading && error && <Card className="p-6"><ErrorState message={error} onRetry={reload} /></Card>}
          {idps.map((idp) => (
            <Card key={idp.id} className="overflow-hidden">
              <div className="flex items-start justify-between gap-3 border-b border-border/60 px-4 py-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{idp.name}</span>
                    <Badge tone={statusTone(idp.status)}>{idp.status}</Badge>
                  </div>
                  <p className="font-mono text-xs text-muted-foreground">
                    {idp.domains.join(', ') || 'No owned domain'}
                  </p>
                </div>
                {canManage && idp.status !== 'disabled' && (
                  <Button variant="ghost" size="icon" aria-label={`Disable ${idp.name}`} onClick={() => setRemoving(idp)}>
                    <Trash2 className="text-destructive" />
                  </Button>
                )}
              </div>
              <div className="px-4 py-4 text-sm text-muted-foreground">
                {idp.status === 'testing' && 'Waiting for the invited owner to complete Microsoft consent and sign-in.'}
                {idp.status === 'active' && 'Connected. This domain can use Microsoft sign-in.'}
                {idp.status === 'disabled' && 'Disabled. Use Connect Microsoft to safely reset it when it has no dependencies.'}
              </div>
            </Card>
          ))}
          {!loading && !error && idps.length === 0 && (
            <Card>
              <EmptyState
                icon={KeyRound}
                title="Microsoft is not connected"
                description="Enter the domain and first owner email. Retink handles the directory and consent steps."
                action={canManage ? <Button onClick={openConnect}>Connect Microsoft</Button> : undefined}
              />
            </Card>
          )}
        </div>
      </div>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect Microsoft</DialogTitle>
            <DialogDescription>
              Retink finds the Microsoft directory from the domain and creates one link for its first owner. No tenant ID needed.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <FormError>{connect.error}</FormError>
            <Field label="Microsoft domain">
              <Input
                value={form.domain}
                required
                autoFocus
                autoCapitalize="none"
                placeholder="retink.ai"
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
              />
            </Field>
            <Field label="First owner email">
              <Input
                type="email"
                value={form.ownerEmail}
                required
                autoCapitalize="none"
                placeholder="owner@retink.ai"
                onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
              />
            </Field>
            <p className="text-xs text-muted-foreground">
              The owner must be a Microsoft directory admin who can approve organization-wide consent.
              {idps.some((idp) => idp.status === 'testing')
                ? ' Creating a replacement link revokes the previous unused owner link.'
                : ''}
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setShow(false)}>Cancel</Button>
              <Button type="submit" disabled={connect.saving}>
                {connect.saving ? 'Creating…' : 'Create owner setup link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDestructive
        open={removing !== null}
        onOpenChange={(open) => { if (!open) setRemoving(null) }}
        title="Disable identity provider"
        description={<>Users will lose Microsoft sign-in through <span className="font-medium">{removing?.name}</span>. The provider is retained for audit and safe recovery.</>}
        confirmPhrase={removing?.name ?? ''}
        confirmLabel="Disable provider"
        onConfirm={async () => {
          if (!removing) return
          if (await removeIdp.mutate(removing.id) === undefined) {
            toast.error(removeIdp.error ?? 'Failed to disable provider')
            return
          }
          setRemoving(null)
          toast.success('Provider disabled')
          reload()
        }}
      />
    </div>
  )
}
